import { useTranslation } from "react-i18next";
import { DefectsTable } from "../components/DefectsTable/DefectsTable";
import { TabPanel, TabView, TabViewTabChangeEvent } from "primereact/tabview";
import { DefectsGraph } from "../components/DefectsGraph/DefectsGraph";
import { errorToast } from "@idwal/idwal-react-components";
import { ANALYTICS_TAB_CLICK, DEFECTS_FLEET_DEFECTS_BY_GRADING_AREA_CLICK, DEFECTS_INSIGHTS, DEFECTS_INSIGHTS_TAB, DEFECTS_PSC_RISK_CLICK, DEFECTS_RECURRING_DEFECTS_CLICK, DEFECTS_SUMMARY, DEFECTS_SUMMARY_TAB } from "../constants/analytics";
import { sendGAEvent } from "../AnalyticsService";
import { DefectsSummary } from "../components/DefectsSummary/DefectsSummary";
import { DefectTabs } from "../types/DefectTabs";
import { resolveTabId, resolveTabIndex } from "../utils/DefectTabHelper";
import { DefectInsights } from "../components/DefectInsights/DefectInsights";
import { PscRiskTab } from "../components/PscRiskTab/PscRiskTab";
import { RecurringDefectsTab } from "../components/RecurringDefectsTab/RecurringDefectsTab";
import { useAppDispatch, useAppSelector } from "../hooks/storeHooks";
import { selectActiveTab, selectDefect } from "../store/selectors/defectSelectors";
import { setActiveTab, setSelectedVessel } from "../store/slices/defectSlice";
import { selectGlobalFiltersLoading } from "../store/selectors/filtersSelectors";
import LOCAL_TABLE_CONFIG_KEY from "../types/TableLocalConfigKeys";
import { useTableSearchParams } from "../hooks/useTableSearchParams";
import { useFlags } from "launchdarkly-react-client-sdk";

const Defects = () => {
    const { t } = useTranslation("locale");
    const dispatch = useAppDispatch();
    const { loading, error } = useAppSelector(selectDefect);
    const activeTab = useAppSelector(selectActiveTab);

    const globalFilterLoading = useAppSelector(selectGlobalFiltersLoading);
    const { releaseRecurringDefects, releasePscMappingCharts } = useFlags();

    useTableSearchParams(LOCAL_TABLE_CONFIG_KEY.DEFECTS, loading);

    if (error) {
        errorToast(t("defectsTable.apiFail"));
        return <div />;
    };

    const isLoading = loading || globalFilterLoading;

    // Built once so ids and rendered indices are mapped in one place.
    const panels = [
        {
            id: DefectTabs.summary,
            header: t("defectsSummary.defectsSummary"),
            content: <DefectsSummary isLoading={isLoading} />
        },
        {
            id: DefectTabs.defects,
            header: t("defectsTable.defectsOverview"),
            content: <DefectsTable isLoading={isLoading} />
        },
        {
            id: DefectTabs.gradingArea,
            header: t("defectsGraph.gradingArea"),
            content: <DefectsGraph isLoading={isLoading} />
        },
        ...(releaseRecurringDefects
            ? [
                  {
                      id: DefectTabs.recurringDefects,
                      header: t("defectsGraph.recurringDefectsTab"),
                      content: <RecurringDefectsTab />
                  }
              ]
            : []),
        {
            id: DefectTabs.insights,
            header: t("defectsGraph.insights"),
            content: <DefectInsights />
        },
        ...(releasePscMappingCharts
            ? [
                  {
                      id: DefectTabs.pscRisk,
                      header: t("defectsGraph.pscRiskTab"),
                      content: <PscRiskTab />
                  }
              ]
            : [])
    ];

    const visibleTabs = panels.map((panel) => panel.id);
    const activeIndex = resolveTabIndex(visibleTabs, activeTab);

    const onTabChange = (e: TabViewTabChangeEvent) => {
        const tab = resolveTabId(visibleTabs, e.index);

        // Trigger window resize event to invoke plotly resize handler on tab change
        window.dispatchEvent(new Event("resize"));
        dispatch(setActiveTab(tab));

        if (activeTab !== tab) {
            dispatch(setSelectedVessel(""));
        }

        switch (tab) {
            case DefectTabs.summary:
                sendGAEvent(DEFECTS_SUMMARY, DEFECTS_SUMMARY_TAB);
                break;
            case DefectTabs.gradingArea:
                sendGAEvent(ANALYTICS_TAB_CLICK, DEFECTS_FLEET_DEFECTS_BY_GRADING_AREA_CLICK);
                break;
            case DefectTabs.recurringDefects:
                sendGAEvent(ANALYTICS_TAB_CLICK, DEFECTS_RECURRING_DEFECTS_CLICK);
                break;
            case DefectTabs.insights:
                sendGAEvent(DEFECTS_INSIGHTS, DEFECTS_INSIGHTS_TAB);
                break;
            case DefectTabs.pscRisk:
                sendGAEvent(ANALYTICS_TAB_CLICK, DEFECTS_PSC_RISK_CLICK);
                break;
            default:
                break;
        }
    };

    return (
        <TabView renderActiveOnly activeIndex={activeIndex} onTabChange={onTabChange} className={"tab-container"}>
            {panels.map((panel) => (
                <TabPanel key={panel.id} header={panel.header}>
                    {panel.content}
                </TabPanel>
            ))}
        </TabView>
    )
};

export default Defects;
